import { useState, useRef, useCallback, useEffect } from 'react';
import { AppState } from 'react-native';

const useTimer = (seconds: number) => {
  const [timeLeft, setTimeLeft] = useState(seconds);
  const [hasTimeout, setHasTimeout] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  const startTimeRef = useRef<number>(Date.now());
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const formatTime = useCallback((time: number): string => {
    const minutes = Math.floor(time / 60);
    const timeSeconds = time % 60;
    return `${minutes}mins : ${timeSeconds.toString().padStart(2, '0')}secs`;
  }, []);

  const updateTimer = useCallback(() => {
    const elapsedTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
    const remainingTime = seconds - elapsedTime;

    if (remainingTime <= 0) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setHasTimeout(true);
      setIsRunning(false);
      setTimeLeft(0);
    } else {
      setTimeLeft(remainingTime);
    }
  }, [seconds]);

  const startTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setIsRunning(true);
    setHasTimeout(false);
    setTimeLeft(seconds);
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(updateTimer, 1000);
  }, [seconds, updateTimer]);

  const resetOtpTimer = useCallback(() => {
    setHasTimeout(false);
    setIsRunning(false);
    setTimeLeft(seconds);
    startTimeRef.current = Date.now();

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, [seconds]);

  const endTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setHasTimeout(true);
    setTimeLeft(0);
    setIsRunning(false);
  }, []);

  useEffect(() => {
    const handleAppStateChange = (nextAppState: string) => {
      if (nextAppState === 'active') {
        const elapsedTime = Math.floor((Date.now() - startTimeRef.current) / 1000);
        const remainingTime = seconds - elapsedTime;
        setTimeLeft(remainingTime > 0 ? remainingTime : 0);
      }
    };

    const subscription = AppState.addEventListener('change', handleAppStateChange);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      subscription.remove();
    };
  }, [seconds]);

  return {
    timeLeft: formatTime(timeLeft),
    hasTimeout,
    isRunning,
    startTimer,
    resetOtpTimer,
    endTimer,
  };
};

export default useTimer;
